// src/steps/Step2LearnerProfile.js
import { AssistantMark, AssistantNote } from "../components/common/AssistantFields";
import React from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Slider,
  Switch,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WordCountTextFieldWithExample from "../components/WordCountTextFieldWithExample";

// ---- Single learner helper texts ----
const SINGLE_MOTIVATION_EXAMPLE = `
The learner wants to move from producing static reports to actively shaping Local Digital Twin projects. He/she sees the need to learn so that they can progress in their career, for example, by better understanding data pipelines, governance and ethics to be able to explain model assumptions.
`.trim();

const SINGLE_MOTIVATION_GUIDING = `
Describe why this learner wants to learn more:

1) Capacity-building goal: What does he/she want to be able to do better or more confidently?
2) Pure Motivation: Is the learner doing it for career progressing purpose? Is the learner motivated by heart to learn something about LDT or is it just an organisational requirement?
`.trim();

const SINGLE_PLACEHOLDERS = {
  job_title: "e.g. Urban Data Analyst (Mobility Department)",
  job_experience: "e.g. Three years working with transport datasets, producing dashboards for traffic flow and public transport KPIs.",
  education: "e.g. Bachelor in Urban Studies and a postgraduate certificate in Applied Data Analytics.",
  motivation: "Click on the 'Show guiding questions' button to see a template for describing the motivation",
  ldt: "Choose the learner\u2019s current level of involvement in Local Digital Twin projects.",
};

// ---- Group of learners helper texts ----
const GROUP_MOTIVATION_EXAMPLE = `
The group of learners come from different departments and need to create a multidisciplinary team to work on the LDT. They are very interested but insecure because they feel that they might not have the requirements for the task ahead, including working with colleagues with different expertises (lack of a common language).
`.trim();

const GROUP_MOTIVATION_GUIDING = `
Describe why this group of learners want to learn more:

1) Capacity-building goal: What do they want to be able to do better or more confidently?
2) Pure Motivation: Are they looking to improve interdisciplinary collaboration? Are they forced by the department manager to fulfill the training? Or are they part of an "innovation department" themselves and have to implement new dynamics into city planning?
`.trim();

const GROUP_PLACEHOLDERS = {
  job_title: "e.g. Mixed group: Urban Planners, Data Analysts, Policy Officers (Mobility & Climate Departments)",
  job_experience: "e.g. The group has 2–10 years of experience across urban planning, data analysis and policy development, with varying exposure to digital tools.",
  education: "e.g. Mix of backgrounds: urban studies, public administration, computer science and environmental engineering.",
  motivation: "Click on the 'Show guiding questions' button to see a template for describing the group's motivation",
  ldt: "Choose the group\u2019s overall level of involvement in Local Digital Twin projects.",
};

export default function Step2LearnerProfile({
  profile,
  setProfile,
  onValidityChange, // optional: hook into App.js stepValidity[2]
}) {
  const handleChange = (field, limit) => (e) => {
    const value = e.target.value;
    // Enforce character limit when provided
    if (limit && value.length > limit) return;
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ---- LDT Connection slider config ----
  const ldtOptions = [
    {
      value: 0,
      key: "not_connected",
      label: "Not connected",
      sentence: "The learner is not connected to Local Digital Twin projects.",
    },
    {
      value: 1,
      key: "somewhat_connected",
      label: "Somewhat connected",
      sentence:
        "The learner is somewhat connected to Local Digital Twin projects.",
    },
    {
      value: 2,
      key: "actively_involved",
      label: "Actively involved",
      sentence:
        "The learner is actively involved in Local Digital Twin projects.",
    },
  ];

  // Deduce slider value from the sentence stored in profile.ldt_connection
  const getLdtSliderValue = () => {
    const idx = ldtOptions.findIndex(
      (opt) => opt.sentence === profile.ldt_connection
    );
    return idx === -1 ? 0 : ldtOptions[idx].value; // default to "Not connected"
  };

  const handleLdtSliderChange = (event, newValue) => {
    const selected =
      ldtOptions.find((opt) => opt.value === newValue) || ldtOptions[0];
    // Store LLM-friendly sentence in profile.ldt_connection
    setProfile((prev) => ({
      ...prev,
      ldt_connection: selected.sentence,
    }));
  };

  const isGroupMode = profile.is_group_mode || false;
  const ph = isGroupMode ? GROUP_PLACEHOLDERS : SINGLE_PLACEHOLDERS;

  return (
    <Box>
      {/* Title + Info Button */}
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ mr: 1 }}>
          Step 2 · Learner Profile
        </Typography>
        <Tooltip
          title={
            <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>
              Choose if you are preparing a learning journey for a single
              learner or for a group.
            </Typography>
          }
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 400,
                padding: 2,
              },
            },
          }}
        >
          <IconButton size="small" sx={{ color: "black", p: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Card wrapping toggle + all form fields */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* Single / Group toggle — card header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 1.5,
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              opacity: isGroupMode ? 0.4 : 1,
              transition: "opacity 0.3s",
            }}
          >
            <PersonIcon fontSize="small" />
            <Typography variant="body2" fontWeight={isGroupMode ? 400 : 700}>
              Single learner
            </Typography>
          </Box>

          <Switch
            checked={isGroupMode}
            onChange={(e) => setProfile((prev) => ({ ...prev, is_group_mode: e.target.checked }))}
            sx={{
              width: 56,
              height: 32,
              padding: 0,
              "& .MuiSwitch-switchBase": {
                padding: "4px",
                "&.Mui-checked": {
                  transform: "translateX(24px)",
                  color: "#fff",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#000",
                    opacity: 1,
                  },
                },
              },
              "& .MuiSwitch-thumb": {
                width: 24,
                height: 24,
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              },
              "& .MuiSwitch-track": {
                borderRadius: 16,
                backgroundColor: "#000",
                opacity: 0.38,
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              opacity: isGroupMode ? 1 : 0.4,
              transition: "opacity 0.3s",
            }}
          >
            <GroupsIcon fontSize="small" />
            <Typography variant="body2" fontWeight={isGroupMode ? 700 : 400}>
              Group of learners
            </Typography>
          </Box>
        </Box>

        {/* ---- FORM FIELDS ---- */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {isGroupMode
            ? "Profile details for preparing Learning Journey for a group"
            : "Profile details for preparing Learning Journey for a single person"}
        </Typography>
        <Grid container spacing={2}>
        {/* Job Title (500 chars max) */}
        <Grid item xs={12}>
          <AssistantMark field="job_title">
          <TextField
            label={`Job title (${profile.job_title.length}/500 characters)`}
            placeholder={ph.job_title}
            fullWidth
            multiline
            minRows={1}
            value={profile.job_title}
            onChange={handleChange("job_title", 500)}
            inputProps={{ maxLength: 500 }}
          />
          </AssistantMark>
        </Grid>

        {/* Experience (500 chars max) */}
        <Grid item xs={12}>
          <AssistantMark field="job_experience">
          <TextField
            label={`Job experience (${profile.job_experience.length}/500 characters)`}
            placeholder={ph.job_experience}
            fullWidth
            multiline
            minRows={1}
            value={profile.job_experience}
            onChange={handleChange("job_experience", 500)}
            inputProps={{ maxLength: 500 }}
          />
          </AssistantMark>
        </Grid>

        {/* Education Background (500 chars max) */}
        <Grid item xs={12}>
          <AssistantMark field="education_background">
          <TextField
            label={`Education background (${profile.education_background.length}/500 characters)`}
            placeholder={ph.education}
            fullWidth
            multiline
            minRows={1}
            value={profile.education_background}
            onChange={handleChange("education_background", 500)}
            inputProps={{ maxLength: 500 }}
          />
          </AssistantMark>
        </Grid>

        {/* LDT Connection slider — own full row between Education and Motivation */}
        <Grid item xs={12}>
          <AssistantNote field={["ldt_connection", "is_group_mode"]} />
          <Box
            sx={{
              position: "relative",
              border: "1px solid rgba(0,0,0,0.23)",
              borderRadius: 1,
              padding: "24px 16px 16px 16px",
              mt: 2,
            }}
          >
            {/* Floating label mimic */}
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: "-8px",
                left: "12px",
                backgroundColor: "white",
                padding: "0 4px",
                fontSize: "0.75rem",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              LDT connection
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              {ph.ldt}
            </Typography>

            {/* Slider for desktop */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Slider
                value={getLdtSliderValue()}
                onChange={handleLdtSliderChange}
                step={1}
                marks={ldtOptions.map((opt) => ({
                  value: opt.value,
                  label: (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: opt.value === getLdtSliderValue() ? 700 : 400,
                        color: opt.value === getLdtSliderValue() ? "text.primary" : "text.secondary",
                        transition: "all 0.2s",
                      }}
                    >
                      {opt.label}
                    </Typography>
                  ),
                }))}
                min={0}
                max={2}
                sx={{
                  display: "block",
                  mx: "auto",
                  width: "60%",
                  mb: 4,
                }}
              />
            </Box>

            {/* Radio buttons for mobile */}
            <Box sx={{ display: { xs: "block", sm: "none" }, mt: 1 }}>
              <RadioGroup
                value={String(getLdtSliderValue())}
                onChange={(e) => handleLdtSliderChange(e, Number(e.target.value))}
              >
                {ldtOptions.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={String(opt.value)}
                    control={<Radio size="small" sx={{ color: "black", "&.Mui-checked": { color: "black" } }} />}
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: opt.value === getLdtSliderValue() ? 700 : 400,
                        }}
                      >
                        {opt.label}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            </Box>
          </Box>
        </Grid>

        {/* Learning Motivation (WordCountTextFieldWithExample) */}
        <Grid item xs={12}>
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {isGroupMode ? "Learning motivation for this group" : "Learning motivation for this learner"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isGroupMode
                ? "Why does the group want or need to embark on this journey?"
                : "Why does the learner want or need to embark on this journey?"}
            </Typography>

            <AssistantMark field="learning_motivation">

            <WordCountTextFieldWithExample
              // label removed to avoid redundancy with the section header
              value={profile.learning_motivation}
              onChange={(val) =>
                setProfile((prev) => ({
                  ...prev,
                  learning_motivation: val,
                }))
              }
              minRows={7}
              minWords={40}
              maxWords={100}
              warnOverBy={10}
              maxCharsPerWord={45}
              placeholder={ph.motivation}
              exampleText={isGroupMode ? GROUP_MOTIVATION_EXAMPLE : SINGLE_MOTIVATION_EXAMPLE}
              exampleTitle={isGroupMode ? "Example group learning motivation" : "Example learning motivation"}
              guidingText={isGroupMode ? GROUP_MOTIVATION_GUIDING : SINGLE_MOTIVATION_GUIDING}
              guidingTitle={isGroupMode ? "Template for describing the group's motivation" : "Template for describing the motivation"}
              onValidityChange={onValidityChange}
            />

            </AssistantMark>
          </Box>
        </Grid>
      </Grid>
        </Box>
      </Box>
    </Box>
  );
}
